import {useState} from "react";
import {message} from "@pankod/refine-antd";
import {generateImage} from "../../../Services/productServices";
import {Product} from "../../../Interfaces";
import {resolveImageUrl, getBestImageUrl} from "./imageUrl";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

interface UseBundleImageOptions {
    products: Product[];
    bundleId?: string | number;
    form: any;
}

/**
 * Shared hook for cover image upload + AI generation.
 * Used by both ProductBundleCreate and ProductBundleEdit.
 */
export function useBundleImage({products, bundleId, form}: UseBundleImageOptions) {
    const [coverImageUrl, setCoverImageUrl] = useState<string | undefined>(undefined);
    const [uploading, setUploading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [aiPrompt, setAiPrompt] = useState<string>("");
    const [showAiPrompt, setShowAiPrompt] = useState(false);

    const buildDefaultPrompt = () => {
        const productNames = (products || []).map(p => p.title).filter(Boolean);
        if (productNames.length === 0) return "";
        return `Create a professional e-commerce bundle product image combining these products: ${productNames.join(", ")}. Set them in a moody, sun-drenched outdoor scene — a warm park or vacation setting with golden afternoon light, soft grass, dappled shadows through trees, and a relaxed holiday atmosphere. No studio equipment, no light stands, no backdrops — natural outdoor setting only. Make it look like a premium lifestyle product photography shot with a warm, inviting mood.`;
    };

    const handleUpload = async (info: any) => {
        const file = info.file;
        if (!file) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("files", file.originFileObj || file);

            // On edit, associate the file with the bundle record
            if (bundleId) {
                formData.append("ref", "product-bundles");
                formData.append("refId", String(bundleId));
                formData.append("field", "coverImage");
            }

            const resp = await fetch(`${REACT_APP_API_URL}/upload`, {
                method: "POST",
                body: formData,
            });

            if (!resp.ok) throw new Error("Upload failed");
            const result = await resp.json();
            const uploadedFile = Array.isArray(result) ? result[0] : result;

            const imgUrl = getBestImageUrl(uploadedFile);
            setCoverImageUrl(imgUrl);
            form.setFieldsValue({coverImage: uploadedFile.id});
            message.success("Image uploaded");
        } catch (err) {
            const errMsg = err instanceof Error ? err.message : String(err);
            message.error("Upload failed: " + errMsg);
        } finally {
            setUploading(false);
        }
    };

    const handleGenerateImage = async () => {
        if (bundleId === undefined) {
            message.warning("Save the bundle first before generating an image");
            return;
        }
        if (!products || products.length === 0) {
            message.warning("Add products to the bundle first");
            return;
        }

        // Collect product image URLs (up to 14)
        const inputReferences = products
            .map(p => {
                const url = p.coverImage?.formats?.medium?.url || p.coverImage?.url;
                return url || null;
            })
            .filter((url): url is string => url !== null)
            .slice(0, 14);

        if (inputReferences.length === 0) {
            message.warning("None of the selected products have images. Add images to the products first or use a text-only prompt.");
        }

        const prompt = aiPrompt.trim() || buildDefaultPrompt();
        if (!prompt) {
            message.warning("Enter a prompt or add products to auto-generate one");
            return;
        }

        setGenerating(true);
        try {
            const resp = await generateImage({
                prompt,
                inputReferences,
                aspectRatio: "1:1",
                resolution: "1K",
                // Only pass ref/refId/field when we have a bundle id (edit mode)
                ...(bundleId ? {
                    ref: "product-bundle",
                    refId: bundleId,
                    field: "coverImage",
                } : {}),
            });

            const result = resp.data;
            if (result?.success && result.imageUrl) {
                setCoverImageUrl(resolveImageUrl(result.imageUrl));
                form.setFieldsValue({coverImage: result.imageId});
                message.success("Image generated successfully");
            } else {
                message.error("Image generation returned no result");
            }
        } catch (err: any) {
            const errMsg = err?.response?.data?.message || err?.message || String(err);
            message.error("AI generation failed: " + errMsg);
        } finally {
            setGenerating(false);
        }
    };

    return {
        coverImageUrl,
        setCoverImageUrl,
        uploading,
        generating,
        aiPrompt,
        setAiPrompt,
        showAiPrompt,
        setShowAiPrompt,
        buildDefaultPrompt,
        handleUpload,
        handleGenerateImage,
    };
}
